import { useMemo } from 'react';

import { selectCarContracts } from '~/components/compliance/detailPage/carContracts';
import useCarHistory from '~/data/hooks/useCarHistory';
import { useContractsForProperty } from '~/data/hooks/useContracts';
import useInvoices from '~/data/hooks/useInvoices';
import { useTickets } from '~/data/hooks/useTickets';
import { InvoiceKind } from '~/data/schemas/invoice';

/**
 * The numbers on the Tickets, Invoices, Contracts and Asset History tab labels.
 *
 * They ride on the tab strip, so they are fetched whether or not those tabs are open —
 * but cheaply: tickets and invoices are single-row list requests read only for their
 * totals, and contracts and history reuse the request their own tab makes, under the
 * same SWR key.
 *
 * `undefined` while a count is in flight, which `tabLabelWithCount` renders as nothing.
 */
export function useCarRelatedCounts({
  carId,
  propertyId,
}: {
  carId: string | undefined;
  propertyId: string | undefined;
}) {
  const cars = useMemo(() => (carId ? [carId] : []), [carId]);
  const enabled = cars.length > 0;

  const { numTickets, isLoading: ticketsLoading } = useTickets({ cars, limit: 1 }, enabled);
  // One read per class: the Invoices tab gives each a subtab of its own, and a combined
  // total can't be split back into them here.
  const { metadata: invoiceMetadata, isLoading: invoicesLoading } = useInvoices(
    { cars, limit: '1', kind: [InvoiceKind.Invoice] },
    enabled,
  );
  const { metadata: proposalMetadata, isLoading: proposalsLoading } = useInvoices(
    { cars, limit: '1', kind: [InvoiceKind.Proposal] },
    enabled,
  );
  const { metadata: creditMetadata, isLoading: creditsLoading } = useInvoices(
    { cars, limit: '1', kind: [InvoiceKind.Credit] },
    enabled,
  );
  const { contracts, isLoading: contractsLoading } = useContractsForProperty(propertyId);
  // No type filter: the number on the tab is everything that has happened to the
  // car, and the filter inside the tab narrows the timeline, not the tab's count.
  const { events, loading: historyLoading } = useCarHistory(carId);

  const contractCount = useMemo(
    () => selectCarContracts(contracts, propertyId, carId).length,
    [contracts, propertyId, carId],
  );

  return {
    tickets: ticketsLoading ? undefined : numTickets,
    invoices: invoicesLoading ? undefined : invoiceMetadata?.numInvoices,
    proposals: proposalsLoading ? undefined : proposalMetadata?.numInvoices,
    credits: creditsLoading ? undefined : creditMetadata?.numInvoices,
    contracts: contractsLoading ? undefined : contractCount,
    history: !enabled || historyLoading ? undefined : events.length,
  };
}
